import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Mail, ShoppingBag, Search, Target, ArrowRight, Sparkles } from 'lucide-react';

interface Service {
  icon: React.ElementType;
  title: string;
  description: string;
  color: string;
  shadowColor: string;
  delay: number;
}

interface ServiceCardProps {
  service: Service;
  index: number;
}

const ServiceCard: React.FC<ServiceCardProps> = ({ service, index }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(cardRef, { 
    once: true, 
    margin: "-10%" 
  });

  const cardVariants = {
    hidden: { 
      opacity: 0,
      scale: 0.8,
    },
    visible: { 
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        delay: index * 0.2,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  return (
    <motion.div
      ref={cardRef}
      variants={cardVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      className="group relative"
    >
      <motion.div 
        className="relative h-full bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-300"
        whileHover={{ y: -3 }}
        transition={{ type: "spring", stiffness: 400, damping: 17 }}
      >
        <motion.div 
          className={`inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-r ${service.color} mb-4 sm:mb-6 text-white`}
          whileHover={{ scale: 1.05 }}
          transition={{ type: "spring", stiffness: 400, damping: 17 }}
        >
          <service.icon className="w-6 h-6 sm:w-8 sm:h-8" />
        </motion.div>

        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">{service.title}</h3>
        <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 leading-relaxed">{service.description}</p>

        <motion.a
          href="#"
          className="inline-flex items-center text-groviyo-yellow font-medium group text-sm sm:text-base"
          whileHover={{ x: 3 }}
          transition={{ type: "spring", stiffness: 400, damping: 17 }}
        >
          Learn More 
          <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
        </motion.a>

        <motion.div 
          className="absolute inset-0 bg-gradient-to-r from-groviyo-yellow/50 to-groviyo-blue/50 rounded-2xl sm:rounded-3xl -z-10"
          initial={{ opacity: 0 }}
          whileHover={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
        />
      </motion.div>
    </motion.div>
  );
};

const ServicesSection: React.FC = () => {
  const services: Service[] = [
    {
      icon: Mail,
      title: "Email & SMS",
      description: "We nurture customer relationships with juicy email and SMS campaigns that keep them coming back for more.",
      color: "from-orange-400 to-yellow-400",
      shadowColor: "rgba(251, 146, 60, 0.3)",
      delay: 0
    },
    {
      icon: ShoppingBag,
      title: "E-commerce Setup",
      description: "Plant the seed to Grow your online store from seedling to thriving with our expert store design and optimization. Shopify | WordPress",
      color: "from-green-400 to-emerald-400",
      shadowColor: "rgba(52, 211, 153, 0.3)",
      delay: 0.1
    },
    {
      icon: Search,
      title: "SEO",
      description: "We will help you get to the core discussion in your market to rank higher with juicy keywords that put your business at the top of the search basket.",
      color: "from-blue-400 to-cyan-400",
      shadowColor: "rgba(56, 189, 248, 0.3)",
      delay: 0.2
    },
    {
      icon: Target,
      title: "Paid Ads",
      description: "We create ad campaigns that are as fresh and vibrant as the fruit they harvest—targeting customers who are ready to bite. We Will Squeeze More Results Out of Your Budget",
      color: "from-purple-400 to-fuchsia-400",
      shadowColor: "rgba(192, 132, 252, 0.3)",
      delay: 0.3
    }
  ];

  return (
    <div className="relative bg-gradient-to-b from-white to-gray-50 py-16 sm:py-24 overflow-hidden">
      <div className="absolute inset-0">
        <motion.div 
          className="absolute top-20 right-0 w-64 sm:w-96 h-64 sm:h-96 bg-groviyo-yellow/10 rounded-full mix-blend-multiply filter blur-xl"
          animate={{
            scale: [1, 1.1, 1],
            rotate: [0, 45, 0],
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div 
          className="absolute bottom-20 -left-20 w-64 sm:w-96 h-64 sm:h-96 bg-groviyo-blue/10 rounded-full mix-blend-multiply filter blur-xl"
          animate={{
            scale: [1.1, 1, 1.1],
            rotate: [45, 0, 45],
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 15
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div 
          className="text-center mb-12 sm:mb-20"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.8 }}
        >
          <motion.div 
            className="inline-flex items-center gap-2 bg-groviyo-yellow/10 px-3 sm:px-4 py-2 rounded-full mb-4 sm:mb-6"
            whileHover={{ scale: 1.02 }}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
          >
            <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-groviyo-yellow" />
            <span className="text-sm sm:text-base text-groviyo-yellow font-medium">Our Services</span>
          </motion.div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
            Grow Your Business With Us
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
            We provide comprehensive digital solutions to help your business flourish in the online marketplace.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
          {services.map((service, index) => (
            <ServiceCard key={index} service={service} index={index} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ServicesSection;